import axios, { AxiosResponse, AxiosError } from "axios";
import { API } from "./utils/HttpRoutes";
import publicApi from "./utils/HttpRequest";
import { getApiPath } from "./utils/RoutesUtils";
import { LoginResponseData } from "../model/LoginData";
import UserData from "../model/UserData";
import {
  CreateSchoolInput,
  SchoolData,
  UpdateSchoolInput,
} from "../model/SchoolData";
import {
  CreateTeacherInput,
  TeacherData,
  UpdateTeacherInput,
} from "../model/TeacherData";

// Response interfaces
export interface ApiResponse<T = any> {
  // data: BaseResponse<T> | null;
  data: T;
  message?: string;
  success?: boolean;
  status: number;
}

export interface ListUser {
  total: number;
  data: UserData[];
}

export interface BaseResponse<T = any> {
  data: T;
  message?: string;
  success?: boolean;
  status: number;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: any;
}

// Function to transform axios response to ApiResponse
function transformResponse<T>(axiosResponse: AxiosResponse): ApiResponse<T> {
  return {
    data: axiosResponse.data,
    message: axiosResponse.data?.message || "Success",
    success: axiosResponse.status >= 200 && axiosResponse.status < 300,
    status: axiosResponse.status,
  };
}

// Function to handle API errors with nested message extraction
function handleApiError(
  error: AxiosError | any,
  defaultMessage: string = "An error occurred",
): ApiResponse<null> {
  if (!(error instanceof AxiosError)) {
    if (typeof error === "string") {
      const parsedError = JSON.parse(error);
      // Validation errors come back as an array of messages.
      const nested = parsedError?.message?.message;
      return {
        data: null,
        message: (Array.isArray(nested) ? nested.join(", ") : nested) || defaultMessage,
        success: false,
        status: parsedError?.statusCode || 500,
      };
    }
    // Extract nested message from error object
    let errorMessage = defaultMessage;
    if (error.message) {
      if (typeof error.message === "object" && error.message.message) {
        errorMessage = error.message.message;
      } else if (typeof error.message === "string") {
        errorMessage = error.message;
      }
    }
    return {
      data: null,
      message: errorMessage,
      success: false,
      status: error.statusCode || 500,
    };
  }

  const status = error.response?.status || 500;
  const responseData = error.response?.data as any;

  // Extract nested message if exists
  let errorMessage = defaultMessage;
  if (responseData?.message) {
    if (
      typeof responseData.message === "object" &&
      responseData.message.message
    ) {
      errorMessage = responseData.message.message;
    } else if (typeof responseData.message === "string") {
      errorMessage = responseData.message;
    }
  } else if (responseData?.error) {
    errorMessage = responseData.error;
  } else if (error.message) {
    errorMessage = error.message;
  }

  return {
    data: null,
    message: errorMessage,
    success: false,
    status,
  };
}

async function login(
  userName: string,
  password: string,
): Promise<ApiResponse<LoginResponseData | null>> {
  const path = getApiPath(API.LOGIN.URL);
  return await publicApi.post(path, { userName, password });
}

// Additional authentication methods
async function logout(): Promise<ApiResponse<null>> {
  try {
    const path = getApiPath(API.LOGOUT.URL);
    const axiosResponse = await publicApi.post(path);
    return transformResponse<null>(axiosResponse);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleApiError(error, "Logout failed") as ApiResponse<null>;
    }

    return {
      data: null,
      message: "Logout failed",
      success: false,
      status: 500,
    };
  }
}

async function getListUsers(
  page: number,
  pageSize: number,
  searchKey?: string,
): Promise<ApiResponse<ListUser | null>> {
  try {
    const params: any = {
      take: pageSize,
      skip: (page - 1) * pageSize,
    };
    if (searchKey) {
      params.search = searchKey;
    }
    const path = getApiPath(API.GET_LIST_USERS.URL, params);
    const axiosResponse = await publicApi.get(path);
    return transformResponse<ListUser>(axiosResponse);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleApiError(
        error,
        "Failed to fetch users",
      ) as ApiResponse<ListUser | null>;
    }

    return {
      data: null,
      message: "Failed to fetch users",
      success: false,
      status: 500,
    };
  }
}

async function getCurrentUser(): Promise<ApiResponse<UserData | null>> {
  try {
    const path = getApiPath(API.GET_CURRENT_USER.URL);
    const axiosResponse = await publicApi.get(path);
    return transformResponse<UserData>(axiosResponse);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleApiError(
        error,
        "Failed to fetch current user",
      ) as ApiResponse<UserData | null>;
    }

    return {
      data: null,
      message: "Failed to fetch current user",
      success: false,
      status: 500,
    };
  }
}

async function createUser(userData: {
  name: string;
  userName: string;
  email: string;
  password: string;
}): Promise<ApiResponse<UserData | null>> {
  try {
    const path = getApiPath(API.CREATE_USER.URL);
    const axiosResponse = await publicApi.post(path, {
      ...userData,
      role: "USER",
    });
    return transformResponse<UserData>(axiosResponse);
  } catch (error) {
    return handleApiError(
      error,
      "Failed to create user",
    ) as ApiResponse<UserData | null>;
  }
}

async function updateUser(
  userId: string,
  userData: {
    name: string;
    password?: string;
  },
): Promise<ApiResponse<UserData | null>> {
  try {
    const path = getApiPath(API.UPDATE_USER.URL.replace(":id", userId));
    const axiosResponse = await publicApi.put(path, userData);
    return transformResponse<UserData>(axiosResponse);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleApiError(
        error,
        "Failed to update user",
      ) as ApiResponse<UserData | null>;
    }

    return {
      data: null,
      message: "Failed to update user",
      success: false,
      status: 500,
    };
  }
}

async function updateUserProfile(
  profileId: string,
  profileData: any,
): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(
      API.UPDATE_USER_PROFILE.URL.replace(":id", profileId),
    );
    const axiosResponse = await publicApi.put(path, profileData);
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleApiError(
        error,
        "Failed to update user profile",
      ) as ApiResponse<any | null>;
    }

    return {
      data: null,
      message: "Failed to update user profile",
      success: false,
      status: 500,
    };
  }
}

async function createUserProfile(
  userId: string,
  profileData: any,
): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(API.CREATE_USER_PROFILE.URL);
    const axiosResponse = await publicApi.post(path, {
      ...profileData,
      userId: userId,
    });
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleApiError(
        error,
        "Failed to create user profile",
      ) as ApiResponse<any | null>;
    }

    return {
      data: null,
      message: "Failed to create user profile",
      success: false,
      status: 500,
    };
  }
}

// async function refreshToken(
//   refreshToken: string
// ): Promise<ApiResponse<AuthTokens | null>> {
//   try {
//     const path = getApiPath(API.REFRESH_TOKEN.URL);
//     const axiosResponse = await publicApi.post(path, { refreshToken });

//     return transformResponse<AuthTokens>(axiosResponse);
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       return handleApiError(error) as ApiResponse<AuthTokens | null>;
//     }

//     return {
//       data: null,
//       message: "Token refresh failed",
//       success: false,
//       status: 500,
//     };
//   }
// }

// ===== Classes =====

async function getClasses(
  schoolId?: string,
): Promise<ApiResponse<any[] | null>> {
  try {
    // schoolId is an admin-only filter; the backend ignores it for others.
    const path = getApiPath(API.GET_CLASSES.URL, { schoolId });
    const axiosResponse = await publicApi.get(path);
    return transformResponse<any[]>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to fetch classes") as ApiResponse<
      any[] | null
    >;
  }
}

async function getClass(id: string): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(API.GET_CLASS.URL, { id });
    const axiosResponse = await publicApi.get(path);
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to fetch class") as ApiResponse<
      any | null
    >;
  }
}

async function createClass(data: {
  name: string;
  schoolId?: string;
}): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(API.CREATE_CLASS.URL);
    const axiosResponse = await publicApi.post(path, data);
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to create class") as ApiResponse<
      any | null
    >;
  }
}

async function updateClass(
  id: string,
  data: { name: string },
): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(API.UPDATE_CLASS.URL, { id });
    const axiosResponse = await publicApi.put(path, data);
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to update class") as ApiResponse<
      any | null
    >;
  }
}

async function deleteClass(id: string): Promise<ApiResponse<null>> {
  try {
    const path = getApiPath(API.DELETE_CLASS.URL, { id });
    await publicApi.delete(path);
    // Backend responds 204 No Content; interceptor unwraps to "" so build success manually.
    return { data: null, message: "Success", success: true, status: 204 };
  } catch (error) {
    return handleApiError(error, "Failed to delete class") as ApiResponse<null>;
  }
}

// ===== Students =====

async function getStudentsByClass(
  classId: string,
): Promise<ApiResponse<any[] | null>> {
  try {
    const path = getApiPath(API.GET_STUDENTS_BY_CLASS.URL, { classId });
    const axiosResponse = await publicApi.get(path);
    return transformResponse<any[]>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to fetch students") as ApiResponse<
      any[] | null
    >;
  }
}

async function createStudent(
  classId: string,
  data: { name: string },
): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(API.CREATE_STUDENT.URL, { classId });
    const axiosResponse = await publicApi.post(path, data);
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to create student") as ApiResponse<
      any | null
    >;
  }
}

async function updateStudent(
  id: string,
  data: {
    name?: string;
    embedding1?: number[];
    embedding2?: number[];
    embedding3?: number[];
  },
): Promise<ApiResponse<any | null>> {
  try {
    const path = getApiPath(API.UPDATE_STUDENT.URL, { id });
    const axiosResponse = await publicApi.put(path, data);
    return transformResponse<any>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to update student") as ApiResponse<
      any | null
    >;
  }
}

async function deleteStudent(id: string): Promise<ApiResponse<null>> {
  try {
    const path = getApiPath(API.DELETE_STUDENT.URL, { id });
    await publicApi.delete(path);
    return { data: null, message: "Success", success: true, status: 204 };
  } catch (error) {
    return handleApiError(
      error,
      "Failed to delete student",
    ) as ApiResponse<null>;
  }
}

// ===== Attendance =====

async function queryAttendance(params: {
  classId: string;
  from?: string;
  to?: string;
}): Promise<ApiResponse<any[] | null>> {
  try {
    const path = getApiPath(API.QUERY_ATTENDANCE.URL, params);
    const axiosResponse = await publicApi.get(path);
    return transformResponse<any[]>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to fetch attendance") as ApiResponse<
      any[] | null
    >;
  }
}

// ===== Schools (admin) =====

export interface ListResult<T> {
  total: number;
  data: T[];
}

async function getSchools(params: {
  take?: number;
  skip?: number;
  search?: string;
}): Promise<ApiResponse<ListResult<SchoolData> | null>> {
  try {
    const path = getApiPath(API.GET_SCHOOLS.URL, params);
    const axiosResponse = await publicApi.get(path);
    return transformResponse<ListResult<SchoolData>>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to fetch schools") as ApiResponse<
      ListResult<SchoolData> | null
    >;
  }
}

async function createSchool(
  data: CreateSchoolInput,
): Promise<ApiResponse<SchoolData | null>> {
  try {
    const path = getApiPath(API.CREATE_SCHOOL.URL);
    const axiosResponse = await publicApi.post(path, data);
    return transformResponse<SchoolData>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to create school") as ApiResponse<
      SchoolData | null
    >;
  }
}

async function updateSchool(
  id: string,
  data: UpdateSchoolInput,
): Promise<ApiResponse<SchoolData | null>> {
  try {
    const path = getApiPath(API.UPDATE_SCHOOL.URL, { id });
    const axiosResponse = await publicApi.put(path, data);
    return transformResponse<SchoolData>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to update school") as ApiResponse<
      SchoolData | null
    >;
  }
}

// ===== Teachers (school accounts) =====

async function getTeachers(params: {
  take?: number;
  skip?: number;
  search?: string;
}): Promise<ApiResponse<ListResult<TeacherData> | null>> {
  try {
    const path = getApiPath(API.GET_TEACHERS.URL, params);
    const axiosResponse = await publicApi.get(path);
    return transformResponse<ListResult<TeacherData>>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to fetch teachers") as ApiResponse<
      ListResult<TeacherData> | null
    >;
  }
}

async function createTeacher(
  data: CreateTeacherInput,
): Promise<ApiResponse<TeacherData | null>> {
  try {
    const path = getApiPath(API.CREATE_TEACHER.URL);
    const axiosResponse = await publicApi.post(path, data);
    return transformResponse<TeacherData>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to create teacher") as ApiResponse<
      TeacherData | null
    >;
  }
}

async function updateTeacher(
  id: string,
  data: UpdateTeacherInput,
): Promise<ApiResponse<TeacherData | null>> {
  try {
    const path = getApiPath(API.UPDATE_TEACHER.URL, { id });
    const axiosResponse = await publicApi.put(path, data);
    return transformResponse<TeacherData>(axiosResponse);
  } catch (error) {
    return handleApiError(error, "Failed to update teacher") as ApiResponse<
      TeacherData | null
    >;
  }
}

async function deleteTeacher(id: string): Promise<ApiResponse<null>> {
  try {
    const path = getApiPath(API.DELETE_TEACHER.URL, { id });
    await publicApi.delete(path);
    return { data: null, message: "Success", success: true, status: 204 };
  } catch (error) {
    return handleApiError(
      error,
      "Failed to delete teacher",
    ) as ApiResponse<null>;
  }
}

const ApiService = {
  login,
  logout,
  getListUsers,
  getCurrentUser,
  createUser,
  updateUser,
  updateUserProfile,
  createUserProfile,
  // refreshToken,
  // Classes
  getClasses,
  getClass,
  createClass,
  updateClass,
  deleteClass,
  // Students
  getStudentsByClass,
  createStudent,
  updateStudent,
  deleteStudent,
  // Attendance
  queryAttendance,
  // Schools
  getSchools,
  createSchool,
  updateSchool,
  // Teachers
  getTeachers,
  createTeacher,
  updateTeacher,
  deleteTeacher,
  // Utility methods
  transformResponse,
  handleApiError,
};

export default ApiService;
